import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import {useEffect, useState} from "react";
import Person from "../../../model/Person";
import SocialSecurityStrategyChange, {
  ISocialSecurityBenefit,
  ISocialSecurityStrategyChange, SocialSecurityStrategy
} from "../../../model/changes/SocialSecurityStrategyChange";
import Income, {IncomeType} from "../../../model/Income";
import {
  ageToStringFormat, dateToLocalFormat, formatAge,
  getISODateFromDate, getISODateTime, isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat, numberToPercentFormat, percentToNumberFormat,
} from "../../../stores/StoreUtilities";
import {useResources} from "../../../stores/ResourceProvider";
import {
  Box,
  DialogContentText,
  FormControlLabel, FormLabel,
  Grid,
  Link,
  Switch, useTheme
} from "@mui/material";
import {addMonths, differenceInMonths} from "date-fns";
import * as React from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreatePlanChangeInput, SnapshotType, UpdatePlanChangeInput} from "../../../API";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Tracking from "../../../components/Tracking";
import LaunchIcon from '@mui/icons-material/Launch';
import SocialSecurityCalc from "../../../components/calculator/SocialSecurityCalc";
import SocialSecurityWidget from "./charts/SocialSecurityWidget";
import Slider from "@mui/material/Slider";
import useDebounce from "../../../components/useDebounce";
import useIsMobile from "../../../components/useIsMobile";
import {FutureCalc} from "../../../components/calculator/Calculator";
import ProjectionErrorAlert from "../ProjectionErrorAlert";

const SocialSecurityStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open? : boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [planModel, setPlanModel] = useState<Plan>(plan)
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [planChange, setPlanChange] = useState<SocialSecurityStrategyChange | undefined>()
  const [person1, setPerson1] = useState<Person | undefined>()
  const [person2, setPerson2] = useState<Person | undefined>()

  const [age1, setAge1] = useState<number | undefined>()
  const [startDate1, setStartDate1] = useState<string | undefined>()
  const [minStartDate1, setMinStartDate1] = useState<Date | undefined>()
  const [amount1, setAmount1] = useState<string | undefined>()
  const [amountHelp1, setAmountHelp1] = useState<string | undefined>()
  const [amountDisable1, setAmountDisable1] = useState<boolean>(false)
  const [fullRetirementBenefit1, setFullRetirementBenefit1] = useState<string | undefined>()
  const [fullRetirementAge1, setFullRetirementAge1] = useState<number>(67)
  const [spousalStart1, setSpousalStart1] = useState<string | undefined>()
  const [spousalBenefit1, setSpousalBenefit1] = useState<string | undefined>()

  const [age2, setAge2] = useState<number | undefined>()
  const [startDate2, setStartDate2] = useState<string | undefined>()
  const [minStartDate2, setMinStartDate2] = useState<Date | undefined>()
  const [amount2, setAmount2] = useState<string | undefined>()
  const [amountHelp2, setAmountHelp2] = useState<string | undefined>()
  const [amountDisable2, setAmountDisable2] = useState<boolean>(false)
  const [fullRetirementBenefit2, setFullRetirementBenefit2] = useState<string | undefined>()
  const [fullRetirementAge2, setFullRetirementAge2] = useState<number>(67)
  const [spousalStart2, setSpousalStart2] = useState<string | undefined>()
  const [spousalBenefit2, setSpousalBenefit2] = useState<string | undefined>()

  const [persons, setPersons] = useState<Person[]>([])
  const [benefits, setBenefits] = useState<ISocialSecurityBenefit[]>([])

  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [applyReduction, setApplyReduction] = useState<boolean>(false)
  const [commitChanges, setCommitChanges] = useState<boolean>(false)

  const [netLiquidAssets, setNetLiquidAssets] = useState<number>(0)
  const [totalTax, setTotalTax] = useState<number>(0)
  const [socialSecurityStrategy, setSocialSecurityStrategy] = useState<SocialSecurityStrategy>(SocialSecurityStrategy.MaximizeBenefits)
  const [isMaximizing, setIsMaximizing] = useState<SocialSecurityStrategy | undefined>()
  const [futureCalc, setFutureCalc] = useState<FutureCalc | undefined>()
  const [calcError, setCalcError] = useState<string | undefined>()

  const { modelStore, calculator, notify } = useResources()
  const isMobile = useIsMobile()
  const theme = useTheme()
  const styles = {
    link: {
      fontSize: 14,
      textDecoration: "none"
    },
    slider: {
      // marginLeft: theme.spacing(1),
      // paddingRight: theme.spacing(-4)
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
  }

  useEffect(() => {
    setPlanModel(plan)
    setIsOpen(open === true)
    let change: SocialSecurityStrategyChange = plan.getChange(PlanChangeType.SocialSecurityStrategy) as SocialSecurityStrategyChange
    if (!change) {
      change = new SocialSecurityStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.SocialSecurityStrategy,
        name: "Social Security Strategy",
        description: "",
        enabled: true,
      })
    }
    setPlanChange(change)
    setSocialSecurityStrategy(change.strategy ?? SocialSecurityStrategy.MaximizeBenefits)
    if (model.person1) {
      const p1 = model.person1
      const timeline = calculator.personTimeline(p1)
      let startDate
      let amount
      let infRate
      setPerson1(p1)
      setFullRetirementAge1(timeline?.fullSocialSecurityAge ?? 67)
      const benefit1 = change.benefits && change.benefits.length > 0 ? change.benefits[0] : undefined
      const other1 = change.benefits && change.benefits.length > 1 ? change.benefits[1] : undefined
      if (benefit1) {
        // A spousal-only person has no personalStart. Their saved spousal start seeds
        // the requested age only when they chose to delay past the other spouse's
        // filing; a start at or before that filing was gated, not chosen, so the
        // request seeds at the earliest age (62) and the effective start re-derives
        // from the other spouse's current filing date.
        let startSeed1 = benefit1.personalStart
        if (!startSeed1 && benefit1.spousalStart) {
          const otherFiled = other1 ? (other1.personalStart || other1.spousalStart || undefined) : undefined
          startSeed1 = (otherFiled && benefit1.spousalStart <= otherFiled)
            ? getISODateFromDate(p1.getDateOnAge(62))
            : benefit1.spousalStart
        }
        if (startSeed1) {
          startDate = startSeed1
          setStartDate1(startDate)
          setFullRetirementBenefit1(numberToMoneyFormat(benefit1.fullBenefit, 0))
        }
        if (benefit1.personalBenefit !== undefined) {
          amount = benefit1.personalBenefit
          setAmount1(numberToMoneyFormat(amount, 0))
          setFullRetirementBenefit1(numberToMoneyFormat(benefit1.fullBenefit, 0))
        }
        if (benefit1.spousalStart) {
          setSpousalStart1(benefit1.spousalStart)
        }
        if (benefit1.spousalBenefit !== 0) {
          setSpousalBenefit1(numberToMoneyFormat(benefit1.spousalBenefit, 0))
        }
        infRate = benefit1.annualInf
      }

      // Get defaults
      if (!startDate || !amount) {
        const income = getSocialSecurityIncome(p1)
        if (income && income.startDate) {
          startDate = getISODateFromDate(income.startDate)
          setStartDate1(startDate)
          setAmount1(numberToMoneyFormat(income.amount,0))
          infRate = income.annualInf
        } else {
          if (timeline?.retireDate) {
            startDate = getISODateFromDate(timeline.retireDate)
            setStartDate1(startDate)
          } else if (timeline?.fullSocialSecurityDate) {
            // Default to full retirement age
            startDate = getISODateFromDate(timeline.fullSocialSecurityDate)
            setStartDate1(startDate)
          }
        }
      }

      const minRetireDate = addMonths(p1.getDateOnAge(62), 0)
      setMinStartDate1(minRetireDate)

      if (startDate) {
        const minRetireIsoDate = getISODateFromDate(minRetireDate)
        if (startDate < minRetireIsoDate) {
          startDate = minRetireIsoDate
          setStartDate1(startDate)
        }
        setAge1(p1.getAgeOnDate(isoToLocalDate(startDate)))
      }

      if (infRate !== undefined) {
        setAnnualInf(numberToPercentFormat(infRate ?? 0, 2))
      } else {
        setAnnualInf(numberToPercentFormat(model.getDefaultInflationRate("Income"), 2))
      }
    }

    if (model.person2) {
      const p2 = model.person2
      const timeline = calculator.personTimeline(p2)
      let startDate
      let amount
      setPerson2(p2)
      setFullRetirementAge2(timeline?.fullSocialSecurityAge ?? 67)

      const benefit2 = change.benefits && change.benefits.length > 1 ? change.benefits[1] : undefined
      const other2 = change.benefits && change.benefits.length > 0 ? change.benefits[0] : undefined
      if (benefit2) {
        // A spousal-only person has no personalStart. Their saved spousal start seeds
        // the requested age only when they chose to delay past the other spouse's
        // filing; a start at or before that filing was gated, not chosen, so the
        // request seeds at the earliest age (62) and the effective start re-derives
        // from the other spouse's current filing date.
        let startSeed2 = benefit2.personalStart
        if (!startSeed2 && benefit2.spousalStart) {
          const otherFiled = other2 ? (other2.personalStart || other2.spousalStart || undefined) : undefined
          startSeed2 = (otherFiled && benefit2.spousalStart <= otherFiled)
            ? getISODateFromDate(p2.getDateOnAge(62))
            : benefit2.spousalStart
        }
        if (startSeed2) {
          startDate = startSeed2
          setStartDate2(startDate)
          setAge2(p2.getAgeOnDate(isoToLocalDate(startDate)))
          setFullRetirementBenefit2(numberToMoneyFormat(benefit2.fullBenefit, 0))
        }
        if (benefit2.personalBenefit !== undefined) {
          amount = benefit2.personalBenefit
          setAmount2(numberToMoneyFormat(amount, 0))
        }
        if (benefit2.spousalStart) {
          setSpousalStart2(benefit2.spousalStart)
        }
        if (benefit2.spousalBenefit !== 0) {
          setSpousalBenefit2(numberToMoneyFormat(benefit2.spousalBenefit, 0))
        }
      }

      // Get defaults
      if (!startDate || !amount) {
        const income = getSocialSecurityIncome(p2)
        if (income && income.startDate) {
          startDate = getISODateFromDate(income.startDate)
          setStartDate2(startDate)
          setAmount2(numberToMoneyFormat(income.amount, 0))
        } else {
          if (timeline?.retireDate) {
            startDate = getISODateFromDate(timeline.retireDate)
            setStartDate2(startDate)
          } else if (timeline?.fullSocialSecurityDate) {
            // Default to full retirement age
            startDate = getISODateFromDate(timeline.fullSocialSecurityDate)
            setStartDate2(startDate)
          }
        }
      }

      const minRetireDate = addMonths(p2.getDateOnAge(62), 0)
      setMinStartDate2(minRetireDate)

      if (startDate) {
        const minRetireIsoDate = getISODateFromDate(minRetireDate)
        if (startDate < minRetireIsoDate) {
          startDate = minRetireIsoDate
          setStartDate2(startDate)
        }
        setAge2(p2.getAgeOnDate(isoToLocalDate(startDate)))
      }
    }

    setPersons(model.persons)
    setApplyReduction(change.applyReduction ?? false)
  }, [model, plan])

  useEffect(() => {
    if (persons.length > 0) {
      calculateBenefits()
    }
  }, [persons])

  useEffect(() => {
    if (!isLoading) {
      calculateBenefits()
    }
  }, [fullRetirementBenefit1, startDate1, fullRetirementBenefit2, startDate2, isLoading] )
  
  const calculateBenefits = () => {
    const benes: ISocialSecurityBenefit[] = []
    let birthDate1: Date | undefined
    let start1: Date | undefined
    let full1 = 0
    if (person1 && startDate1) {
      birthDate1 = isoToLocalDate(person1.birthDate)
      start1 = isoToLocalDate(startDate1)
      if (start1) {
        setAge1(person1.getAgeOnDate(start1))
      }
      full1 = moneyToNumberFormat(fullRetirementBenefit1)
    }
    let birthDate2: Date | undefined
    let start2: Date | undefined
    let full2 = 0
    if (person2 && startDate2) {
      birthDate2 = isoToLocalDate(person2.birthDate)
      start2 = isoToLocalDate(startDate2)
      if (start2) {
        setAge2(person2.getAgeOnDate(start2))
      }
      full2 = moneyToNumberFormat(fullRetirementBenefit2)
    }

    if (birthDate1 && start1) {
      const benefits = SocialSecurityCalc.calculateRetirementBenefits(birthDate1, start1, full1, birthDate2, start2, full2)
      if (benefits.myStart) {
        setAmount1(numberToMoneyFormat(benefits.myBenefit, 0))
        setAmountDisable1(false)
        if (benefits.spousalStart) {
          setAmountHelp1(benefits.myDescription)
          setSpousalStart1(getISODateFromDate(benefits.spousalStart))
          setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
        } else {
          setAmountHelp1(benefits.myDescription)
          setSpousalStart1(undefined)
          setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
        }
      } else if (benefits.spousalStart) {
        setAmount1(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable1(true)
        setAmountHelp1(benefits.myDescription)
        setSpousalStart1(getISODateFromDate(benefits.spousalStart))
        setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
      }
      benes.push({
        personId: person1!.id,
        fullBenefit: full1,
        personalStart: benefits.myStart ? getISODateFromDate(benefits.myStart) : undefined,
        personalBenefit: benefits.myBenefit,
        personalPercent: benefits.myPercent,
        spousalStart: benefits.spousalStart ? getISODateFromDate(benefits.spousalStart) : undefined,
        spousalBenefit: benefits.spousalStart ? benefits.spousalBenefit : 0,
        spousalPercent: benefits.spousalStart ? benefits.spousalPercent : 0,
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    } else if (birthDate1 && ((startDate1 && amount1) || (spousalStart1 && spousalBenefit1))) {
      benes.push({
        personId: person1!.id,
        fullBenefit: 0,
        personalStart: startDate1,
        personalBenefit: moneyToNumberFormat(amount1, 0),
        spousalStart: spousalStart1,
        spousalBenefit: moneyToNumberFormat(spousalBenefit1, 0),
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    }

    if (birthDate2 && start2) {
      const benefits = SocialSecurityCalc.calculateRetirementBenefits(birthDate2, start2, full2, birthDate1, start1, full1)
      if (benefits.myStart) {
        setAmount2(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable2(false)
        if (benefits.spousalStart) {
          setAmountHelp2(benefits.myDescription)
          setSpousalStart2(getISODateFromDate(benefits.spousalStart))
          setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
        } else {
          setAmountHelp2(benefits.myDescription)
          setSpousalStart2(undefined)
          setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
        }
      } else if (benefits.spousalStart) {
        setAmount2(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable2(true)
        setAmountHelp2(benefits.myDescription)
        setSpousalStart2(getISODateFromDate(benefits.spousalStart))
        setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
      }
      benes.push({
        personId: person2!.id,
        fullBenefit: full2,
        personalStart: benefits.myStart ? getISODateFromDate(benefits.myStart) : undefined,
        personalBenefit: benefits.myBenefit,
        personalPercent: benefits.myPercent,
        spousalStart: benefits.spousalStart ? getISODateFromDate(benefits.spousalStart) : undefined,
        spousalBenefit: benefits.spousalStart ? benefits.spousalBenefit : 0,
        spousalPercent: benefits.spousalStart ? benefits.spousalPercent : 0,
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    } else if (birthDate2 && ((startDate2 && amount2) || (spousalStart2 && spousalBenefit2))) {
      benes.push({
        personId: person2!.id,
        fullBenefit: 0,
        personalStart: startDate2,
        personalBenefit: moneyToNumberFormat(amount2, 0),
        spousalStart: spousalStart2,
        spousalBenefit: moneyToNumberFormat(spousalBenefit2, 0),
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    }
    setBenefits(benes)
    setTotalTax(0)
    setNetLiquidAssets(0)
    debounceCalculateFuture(benes)
    setIsLoading(false)
  }

  const calculateFuture = async (benefits: ISocialSecurityBenefit[]) => {
    // console.debug(`calculateFuture: ${(new Date()).toISOString()}`)
    if (planChange) {
      // Recalc future
      const change = new SocialSecurityStrategyChange({
        id: planChange.id,
        accountId: planChange.accountId,
        userId: planChange.userId,
        modelId: planChange.modelId,
        planId: planChange.planId,
        changeType: planChange.changeType,
        enabled: true,
        details: JSON.stringify({benefits: benefits, applyReduction: applyReduction})
      })
      const endYear = person1?.latestLifeExpectancyYear
      const modelCopy = modelStore.cloneModel(model.id)
      if (modelCopy) {
        const planCopy = modelCopy.findPlan(plan.id)
        if (planCopy) {
          planCopy.setChange(change)
          planCopy.applyChanges(modelCopy)
          modelStore.addCurrentSnapshot(modelCopy, SnapshotType.Combined)
          modelCopy.updatedAt = getISODateTime()
          const futureCalc = calculator.calculateFuture(modelCopy, "Social Security Strategy", endYear)
          // A preview that can't be calculated shows its error, and the strategy can't be saved from it
          setCalcError(futureCalc.calcError)
          setNetLiquidAssets(futureCalc.calcError ? 0 : futureCalc.netLiquidAssets)
          setTotalTax(futureCalc.calcError ? 0 : futureCalc.totalTax)
          setFutureCalc(futureCalc.calcError ? undefined : futureCalc)
        }
      }
    }
  }

  const debounceCalculateFuture = useDebounce((benefits: ISocialSecurityBenefit[]) => calculateFuture(benefits), 100)

  const calculateNetWorth = (benefits: ISocialSecurityBenefit[]) => {
    let netWorth = 0

    if (planChange) {
      // Recalc future
      const change = new SocialSecurityStrategyChange({
        id: planChange.id,
        accountId: planChange.accountId,
        userId: planChange.userId,
        modelId: planChange.modelId,
        planId: planChange.planId,
        changeType: planChange.changeType,
        enabled: true,
        details: JSON.stringify({benefits: benefits, applyReduction: applyReduction})
      })
      const endYear = person1?.latestLifeExpectancyYear
      const modelCopy = modelStore.cloneModel(model.id)

      if (modelCopy) {
        const planCopy = modelCopy.findPlan(plan.id)
        if (planCopy) {
          planCopy.setChange(change)
          planCopy.applyChanges(modelCopy)
          modelStore.addCurrentSnapshot(modelCopy, SnapshotType.Combined)
          modelCopy.updatedAt = getISODateTime()
          const futureCalc = calculator.calculateFuture(modelCopy, "Social Security Strategy", endYear)
          // A failed candidate ends the search; it is never scored as $0 net worth
          if (futureCalc.calcError) throw new Error(futureCalc.calcError)
          netWorth = futureCalc.netLiquidAssets
        }
      }
    }
    return netWorth
  }

  // const debounceCalculateNetWorth = useDebounce((benefits: ISocialSecurityBenefit[]) => calculateNetWorth(benefits), 200)

  const maximizeBenefits = async (option: SocialSecurityStrategy) => {
    const start = new Date()
    setIsMaximizing(option)
    setSocialSecurityStrategy(option)
    calculator.enableOccurencesCache()

    const netWorthCalc = (option === SocialSecurityStrategy.MaximizeNetworth) ? calculateNetWorth : undefined

    let timer: any
    timer = setTimeout(() => {
      try {
        const maxBenefits = SocialSecurityCalc.maximizeRetirementBenefits(persons, moneyToNumberFormat(fullRetirementBenefit1, 0), percentToNumberFormat(annualInf, 0), moneyToNumberFormat(fullRetirementBenefit2, 0), netWorthCalc)
        if (maxBenefits) {
          // console.log(`Max Benefits:`, maxBenefits)
          setStartDate1(maxBenefits[0].personalStart ?? maxBenefits[0].spousalStart)
          if (maxBenefits.length > 1) {
            setStartDate2(maxBenefits[1].personalStart ?? maxBenefits[1].spousalStart)
          }
        }
      } catch (err: any) {
        // A candidate projection failed: keep the current dates and show why
        setCalcError(err.message)
      } finally {
        setIsMaximizing(undefined)
        calculator.disableOccurencesCache()
      }
      clearTimeout(timer);
      const end = new Date()
      console.log(`Maximizing took ${(end.getTime() - start.getTime())/1000} seconds`)
    }, 200);
  }

  const getSocialSecurityIncome = (person: Person): Income | undefined => {
    const income = model.incomes.find((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person.id && !i.archived)
    return income
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    if (calcError) {
      notify.show('error', calcError)
      return
    }
    try {
      let change: PlanChange | undefined

      // A spousal benefit cannot begin before the other spouse has filed. The calculated
      // benefit states can no longer violate this, but stale raw state could — block it.
      if ((spousalStart1 && startDate2 && spousalStart1 < startDate2) ||
          (spousalStart2 && startDate1 && spousalStart2 < startDate1)) {
        notify.show('error', "A spousal benefit cannot begin before the spouse files for their own benefit. Adjust the start ages and try again.")
        return
      }

      const benefits: ISocialSecurityBenefit[] = []
      if (startDate1 && person1) {
        benefits.push({personId: person1.id,
          fullBenefit: moneyToNumberFormat(fullRetirementBenefit1, 0),
          personalStart: amountDisable1 ? undefined : startDate1,
          personalBenefit: amountDisable1 ? 0 : moneyToNumberFormat(amount1),
          spousalStart: spousalStart1,
          spousalBenefit: spousalStart1 ? moneyToNumberFormat(spousalBenefit1, 0) : 0,
          annualInf: percentToNumberFormat(annualInf, 2)
        })
      }
      if (startDate2 && person2) {
        benefits.push({personId: person2.id,
          fullBenefit: moneyToNumberFormat(fullRetirementBenefit2, 0),
          personalStart: amountDisable2 ? undefined : startDate2,
          personalBenefit: amountDisable2 ? 0 : moneyToNumberFormat(amount2),
          spousalStart: spousalStart2,
          spousalBenefit: spousalStart2 ? moneyToNumberFormat(spousalBenefit2, 0) : 0,
          annualInf: percentToNumberFormat(annualInf, 2)
        })
      }
      const details: ISocialSecurityStrategyChange = {
        strategy: socialSecurityStrategy,
        benefits: benefits,
        applyReduction: applyReduction
      }
      
      if (planChange && !planChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: planChange?.accountId,
          userId: planChange.userId,
          modelId: planChange.modelId,
          planId: planChange.planId,
          changeType: planChange.changeType,
          name: planChange.name,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.createPlanChange(input, true)
        Tracking.event({action: "Social Security Strategy Created"})
      } else if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Social Security Strategy Updated"})
      }

      if (change && commitChanges) {
        await change.commit(model, modelStore)
      }
      
      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    let array: string[] = []
    if (person1) {
      if (startDate1 && amount1 !== "$0" && !amountDisable1) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(amount1), 0)
        array.push(`${person1!.nickname}: ${amount} at age ${ageToStringFormat(age1)}`)
      }
      if (spousalStart1 && spousalBenefit1) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(spousalBenefit1), 0)
        const age = person1.getAgeOnDate(isoToLocalDate(spousalStart1))
        array.push(`${person1!.nickname}: ${amount} at age ${ageToStringFormat(age)}`)
      }
    }
    if (person2) {
      if (startDate2 && amount2 !== "$0" && !amountDisable2) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(amount2), 0)
        array.push(`${person2!.nickname}: ${amount} at age ${ageToStringFormat(age2)}`)
      }
      if (spousalStart2 && spousalBenefit2) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(spousalBenefit2), 0)
        const age = person2.getAgeOnDate(isoToLocalDate(spousalStart2))
        array.push(`${person2!.nickname}: ${amount} at age ${ageToStringFormat(age)}`)
      }
    }
    if (applyReduction) {
      array.push(`Apply projected benefit reduction`)
    }
    return array.join(", ")
  }


  const agetext = (value: number) => {
    const years = Math.floor(value / 12)
    const months = value % 12
    if (months === 0) {
      return String(years)
    } else {
      return `${years}, ${months}mo`
    }
  }

  const renderAgeSlider = (person: Person, start: string, num: number) => {
    const marks = [
      {value: 62*12, label:"62"},
      {value: 63*12, label:"63"},
      {value: 64*12, label:"64"},
      {value: 65*12, label:"65"},
      {value: 66*12, label:"66"},
      {value: 67*12, label:"67"},
      {value: 68*12, label:"68"},
      {value: 69*12, label:"69"},
      {value: 70*12, label:"70"},
    ]

    const birthDate = isoToLocalDate(person.birthDate)
    const birthDay = birthDate.getDate()
    let minMonths = 62*12
    if (birthDay > 2) {
      minMonths = 62*12 //+1
      marks[0] = {value: minMonths, label:"62"}
    }
    const fra = num === 1 ? fullRetirementAge1 : fullRetirementAge2
    const startDate = isoToLocalDate(start)
    const months = Math.max(Math.min(differenceInMonths(startDate, birthDate), 70*12), minMonths)
    const stepSize = 1

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" flexGrow={1}>
          <FormLabel sx={styles.formLabel}>{person.nickname}'s Start Age {`(${agetext(months)} on ${dateToLocalFormat(startDate)})`}</FormLabel>
          <FormLabel sx={styles.formLabel}>{isMobile ? "FRA:" : "Full Retirement Age:"} {formatAge(fra)}</FormLabel>
        </Box>
        <Slider
          aria-label="Age slider"
          value={months}
          getAriaValueText={agetext}
          step={stepSize}
          min={minMonths}
          max={70*12}
          marks={marks}
          size="small"
          color={num === 1 ? "primary" : "secondary"}
          valueLabelDisplay="auto"
          valueLabelFormat={agetext}
          sx={styles.slider}
          onChange={(event: Event, value: number | number[], activeThumb: number) => {
            // console.log(`onChangeSlider(${value}, ${activeThumb}`)
            const date = addMonths(birthDate, value as number)
            if (num === 1) {
              setStartDate1(getISODateFromDate(date))
            } else {
              setStartDate2(getISODateFromDate(date))
            }
          }}
        />
      </Box>
    )
  }

  // A person with no work record (no PIA) still gets a start age slider, but it is a
  // request, not a guarantee: their spousal benefit actually begins at the latest of
  // the requested age, the other spouse's filing date, and their own age 62. Choosing
  // a later age matters when the earner files before this person's FRA — claiming
  // immediately pays a reduced spousal benefit, waiting until FRA pays the full 50%.
  // The caption always shows the effective start the engine computed.
  const renderSpousalOnlyRow = (person: Person, earner: Person, num: number) => {
    const marks = [
      {value: 62*12, label:"62"},
      {value: 63*12, label:"63"},
      {value: 64*12, label:"64"},
      {value: 65*12, label:"65"},
      {value: 66*12, label:"66"},
      {value: 67*12, label:"67"},
      {value: 68*12, label:"68"},
      {value: 69*12, label:"69"},
      {value: 70*12, label:"70"},
    ]

    const fra = num === 1 ? fullRetirementAge1 : fullRetirementAge2
    const requestedIso = num === 1 ? startDate1 : startDate2
    const earnerIso = num === 1 ? startDate2 : startDate1
    const effectiveIso = (num === 1 ? spousalStart1 : spousalStart2) ?? earnerIso

    const birthDate = isoToLocalDate(person.birthDate)
    const requestedDate = requestedIso ? isoToLocalDate(requestedIso) : undefined
    const months = requestedDate
      ? Math.max(Math.min(differenceInMonths(requestedDate, birthDate), 70*12), 62*12)
      : 62*12

    // The effective start rides in the label, matching the "(age on date)" pattern of
    // the regular start age slider; a small note appears only when the start is gated
    let labelDetail = ""
    let gatedNote: string | undefined
    if (effectiveIso) {
      const effectiveDate = isoToLocalDate(effectiveIso)
      const effectiveMonths = Math.max(differenceInMonths(effectiveDate, birthDate), 0)
      labelDetail = ` (${agetext(effectiveMonths)} on ${dateToLocalFormat(effectiveDate)})`
      if (requestedIso && requestedIso < effectiveIso) {
        gatedNote = effectiveIso === earnerIso
          ? `Begins when ${earner.nickname} files`
          : "Earliest spousal start is age 62"
      }
    } else {
      gatedNote = `Begins when ${earner.nickname} files for their benefit`
    }

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" flexGrow={1}>
          <FormLabel sx={styles.formLabel}>{person.nickname}'s Spousal Benefit Start Age{labelDetail}</FormLabel>
          <FormLabel sx={styles.formLabel}>{isMobile ? "FRA:" : "Full Retirement Age:"} {formatAge(fra)}</FormLabel>
        </Box>
        {gatedNote &&
          <Box>
            <FormLabel sx={styles.formLabel}>{gatedNote}</FormLabel>
          </Box>
        }
        <Slider
          aria-label="Spousal age slider"
          value={months}
          getAriaValueText={agetext}
          step={1}
          min={62*12}
          max={70*12}
          marks={marks}
          size="small"
          color={num === 1 ? "primary" : "secondary"}
          valueLabelDisplay="auto"
          valueLabelFormat={agetext}
          sx={styles.slider}
          onChange={(event: Event, value: number | number[], activeThumb: number) => {
            const date = addMonths(birthDate, value as number)
            if (num === 1) {
              setStartDate1(getISODateFromDate(date))
            } else {
              setStartDate2(getISODateFromDate(date))
            }
          }}
        />
      </Box>
    )
  }

  if (!planChange) {
    return null
  }

  // A person is spousal-only when they have no PIA of their own and their spouse does
  const pia1 = moneyToNumberFormat(fullRetirementBenefit1, 0)
  const pia2 = moneyToNumberFormat(fullRetirementBenefit2, 0)
  const spousalOnly1 = Boolean(person2) && !pia1 && pia2 > 0
  const spousalOnly2 = Boolean(person1) && !pia2 && pia1 > 0

  const commitDisabled = !(startDate1 || amount1 || startDate2 || amount2)
  const reductionEntry = SocialSecurityStrategyChange.reductionSchedule.entries().next()
  const reductionYear = reductionEntry.value ? reductionEntry.value[0] : 0
  const reductionPercent = reductionEntry.value ? reductionEntry.value[1] : 0

  return (
    <ModelEditDialog title="Social Security Strategy" open={isOpen} size="md" fullWidth={false}
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      {/*<DialogContentText>*/}
      {/*  Adjust Social Security start dates and benefit amounts.*/}
      {/*</DialogContentText>*/}
      <ProjectionErrorAlert message={calcError}/>
      <DialogContentText sx={{fontSize:14, mt:1, position: "relative", zIndex: 1}}>
        Find your Full Retirement Age Benefits (PIA) at: <Link href="https://www.ssa.gov/myaccount/statement.html" target="_blank" sx={styles.link}>
        Social Security Statement</Link>, or see <Link href="https://www.ssa.gov/OACT/anypia/index.html" target="_blank" sx={styles.link}>
        Social Security Benefit Calculators</Link>.
      </DialogContentText>
      <Grid container spacing={0}>
        {benefits.length > 0 &&
          <Grid item xs={12}>
             <SocialSecurityWidget strategy={socialSecurityStrategy} benefits={benefits} persons={persons}
                                   annualInf={percentToNumberFormat(annualInf)}
                                   applyReduction={applyReduction}
                                   totalTax={totalTax}
                                   netLiquidAssets={netLiquidAssets}
                                   onMaximize={maximizeBenefits}
                                   isMaximizing={isMaximizing}
                                   futureCalc={futureCalc}
             />
          </Grid>
        }
        {person1 && startDate1 && [
          <Grid item xs={12} sm={4} sx={isMobile ? {pl:0} : {pl:0, pr:2}} key="person1-pia">
            <TextFieldValidator
              type="string"
              validators={{ required: false, minMoney: 0, isMoney: true }}
              name="fullRetirementBenefit1"
              variant="standard"
              margin="dense"
              fullWidth
              label={`${person1.nickname}'s Retirement Benefit (PIA)`}
              value={fullRetirementBenefit1}
              helperText="Enter in today's dollars"
              // helperText={`Full Retirement Age: ${formatAge(fullRetirementAge1)}`}
              InputLabelProps={{shrink: Boolean(fullRetirementBenefit1)}}
              onChange={(event: any) => {
                setFullRetirementBenefit1(event.target.value ?? 0)
                setIsLoading(false)
              }}
            />
          </Grid>,
          <Grid item xs={12} sm={8} sx={isMobile ? {pl:0, pr:0, pt:"14px"} : {pl:1, pr:1, pt:"14px"}} key="person1-slider">
            {spousalOnly1 && person2 ? renderSpousalOnlyRow(person1, person2, 1) : renderAgeSlider(person1, startDate1, 1)}
          </Grid>
        ]}

        {person2 && startDate2 && [
          <Grid item xs={12} sm={4} sx={isMobile ? {pl:0} : {pl:0, pr:2}} key="person2-pia">
            <TextFieldValidator
              type="string"
              validators={{ required: false, minMoney: 0, isMoney: true }}
              name="fullRetirementBenefit2"
              variant="standard"
              margin="dense"
              fullWidth
              label={`${person2.nickname}'s Full Retirement Benefit (PIA)`}
              value={fullRetirementBenefit2}
              helperText="Enter in today's dollars"
              // helperText={`Full Retirement Age: ${formatAge(fullRetirementAge2)}`}
              InputLabelProps={{shrink: Boolean(fullRetirementBenefit2)}}
              onChange={(event: any) => {
                setFullRetirementBenefit2(event.target.value ?? 0)
                setIsLoading(false)
              }}
            />
          </Grid>,
          <Grid item xs={12} sm={8} sx={isMobile ? {pl:0, pr:0, pt:"14px"} : {pl:1, pr:1, pt:"14px"}} key="person2-slider">
            {spousalOnly2 && person1 ? renderSpousalOnlyRow(person2, person1, 2) : renderAgeSlider(person2, startDate2, 2)}
          </Grid>
        ]}

        <Grid item xs={12} sm={4} sx={isMobile? {pl:0} : {pl:0, pr:2}}>
          <TextFieldValidator
            type="string"
            validators={{ required: false, minValue: 0 }}
            name="annualInf"
            variant="standard"
            margin="dense"
            fullWidth
            label="Annual Inflation %"
            value={annualInf}
            onChange={(event: any) => setAnnualInf(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          {!isMobile &&
            <FormControlLabel control={
              <Switch checked={applyReduction}
                      onChange={(event: any) => {
                        setApplyReduction(event.target.checked)
                      }}
              />}
              label={<div style={{display:"flex", alignItems:"center"}}>Apply projected {numberToPercentFormat(-reductionPercent, 0)} benefit reduction in {reductionYear}&nbsp;
                <a href="https://www.ssa.gov/news/press/releases/2025/#2025-06-18" target="_blank" rel="noreferrer"><LaunchIcon/></a></div>}
              componentsProps={{
                typography: {
                  variant:"body2"
                }}
              }
              sx={{my:0, py:0, maxHeight:32}}
            />
          }
          <FormControlLabel control={
            <Switch checked={commitChanges}
                      disabled={commitDisabled}
                      onChange={(event: any) => {
                        setCommitChanges(event.target.checked)
                      }}
            />}
            label={`Permanently apply these changes to ${model.name}.`}
            componentsProps={{
                typography: {
                  variant:"subtitle2"
              }}
            }
            sx={{my:0, py:0, maxHeight:32}}
          />
        </Grid>
      </Grid>
    </ModelEditDialog>
  )
}
export default SocialSecurityStrategyDialog